import { createHash } from 'crypto';
import { cloneDeep, isString, isArray, isObject } from 'lodash';

export type CleanableValue =
  | string
  | CleanableValue[]
  | Record<string, unknown>
  | null;

export const truncateContent = (content: string) => {
  const cutContent = content.substring(0, 300);
  return cutContent.concat('...');
};

export const generateInitials = (name: string) => {
  const nameParts = name.split(' ');
  if (nameParts.length > 1) {
    return nameParts[0][0] + nameParts[nameParts.length - 1][0];
  }
  return nameParts[0][0];
};

export const capitalize = (value: CleanableValue): CleanableValue => {
  if (isString(value)) return value.toUpperCase();
  if (isArray(value))
    return value.map((item: unknown) => capitalize(item as CleanableValue));
  if (isObject(value)) {
    return Object.keys(value).reduce(
      (acc, key) => {
        acc[key] = capitalize(value[key] as CleanableValue);
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }
  return value;
};

export const removeWhiteSpace = (value: CleanableValue): CleanableValue => {
  if (isString(value)) return value.trim();
  if (isArray(value))
    return value.map((item: unknown) =>
      removeWhiteSpace(item as CleanableValue),
    );
  if (isObject(value)) {
    return Object.keys(value).reduce(
      (acc, key) => {
        acc[key] = removeWhiteSpace(value[key] as CleanableValue);
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }
  return value;
};

export const removeNullAndEmptyValues = (
  value: CleanableValue,
): CleanableValue => {
  if (isString(value)) return value;
  if (isArray(value)) {
    return value
      .map((item: unknown) => removeNullAndEmptyValues(item as CleanableValue))
      .filter((item: unknown) => {
        if (item === null || item === undefined) return false;
        if (isString(item) && (item as string).trim() === '') return false;
        return true;
      });
  }
  if (isObject(value)) {
    return Object.keys(value).reduce(
      (acc, key) => {
        const processedValue = removeNullAndEmptyValues(
          value[key] as CleanableValue,
        );
        if (processedValue === null || processedValue === undefined) return acc;
        if (
          isString(processedValue) &&
          (processedValue as string).trim() === ''
        )
          return acc;
        acc[key] = processedValue;
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }
  return value;
};

export const cleanData = (value: CleanableValue): CleanableValue => {
  const valueClone = cloneDeep(value);

  if (isString(valueClone)) {
    const processed = valueClone.trim().toUpperCase();
    return processed === '' ? null : processed;
  }

  if (isArray(valueClone)) {
    return valueClone
      .map((item: unknown) => cleanData(item as CleanableValue))
      .filter((item: unknown) => {
        if (item === null || item === undefined) return false;
        if (isString(item) && (item as string).trim() === '') return false;
        return true;
      });
  }

  if (isObject(valueClone)) {
    return Object.keys(valueClone).reduce(
      (acc, key) => {
        const processedValue = cleanData(valueClone[key] as CleanableValue);
        if (processedValue === null || processedValue === undefined) {
          return acc;
        }
        if (
          isString(processedValue) &&
          (processedValue as string).trim() === ''
        ) {
          return acc;
        }

        acc[key] = processedValue;
        return acc;
      },
      {} as Record<string, unknown>,
    );
  }

  return valueClone;
};

export const generateSignature = (
  values: string[],
  separator = '|',
  algorithm = 'sha512',
) => {
  const data = values.join(separator);
  return createHash(algorithm).update(data).digest('hex');
};

export const generateHash = (params: Record<string, unknown> = {}) => {
  const secretKey = process.env['HASH_SECRET_KEY'] ?? '';

  const queryString: string = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join('&');

  return generateSignature([queryString, secretKey]);
};
